import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import IResponseBaseTDto from '../../API/Interfaces/IResponseBaseTDto';
import ILocationDetailsDto from '../../API/Interfaces/ILocationDetailsDto';
import LocationsServiceAPI from '../../API/Services/LocationServiceAPI';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import ISearchBaseDto from '../../API/Interfaces/ISearchBaseDto';
import EntityDetails, { IEntityDetailsProps, IEntityDetailsBaseProps } from '../../Layout/EntityDetails';
import DetailsBox from '../../Layout/Components/DetailsBox';
import {
	LocationDetailsIcon,
	LocationChainIcon,
	LocationCodeIcon,
	LocationDevicesIcon,
	GppGoodOutlinedIcon,
	VoucherIcon,
	PriceTagIcon,
} from '../../Layout/Components/Icons';
import { IDevicesBasicDto } from '../../API/Interfaces/IDeviceDtos';
import DetailsError from '../../Layout/Components/DetailsError';
import EntityTabs, { IEntityTabsProps } from '../../Layout/Components/EntityTabs/EntityTabs';
import AssignmentOutlinedIcon from '@material-ui/icons/AssignmentOutlined';
import CropOriginalIcon from '@material-ui/icons/CropOriginal';
import { LocationGoals } from './LocationGoals/LocationGoals';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import LocationRules from './LocationRules/LocationRules';
import { LocationGraphics } from './LocationGraphics/LocationGraphics';
import LocationVouchers from './LocationVouchers';
import { LocationReports } from './LocationReports/LocationReports';

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		details: {
			width: '50%',
		},
	})
);

export const LocationDetails = (props: IEntityDetailsBaseProps) => {
	const { enqueueSnackbar } = useSnackbar();
	const { t } = useTranslation();

	const { id } = useParams<{ id: string }>();
	const [entity, setEntity] = useState(props.entity);
	const [noData, setNoData] = useState(false);
	const [devices, setDevices] = useState<IDevicesBasicDto[]>([]);
	const classes = useStyles();
	const errorDialog = (message?: string) =>
		enqueueSnackbar(`${t('common.errorOccured')}${message ? ': ' : ''}${message && message}`, { variant: 'error' });

	const iconStyle = {
		width: '21',
		height: '26',
	};

	const svgColorFill = '#000000';

	const fetchEntity = async function (id: string) {
		try {
			const response: IResponseBaseTDto<ILocationDetailsDto> = await LocationsServiceAPI.GetOne(id);
			if (response) return response;
		} catch (e) {
			errorDialog(e.message);
		}
		return undefined;
	};

	const fetchDevices = async function () {
		try {
			const request: ISearchBaseDto = {
				itemsPerPage: 10,
				pageNumber: 1,
				searchPhrase: '',
				sortColumn: '',
				sortDirection: '',
			};
			const response = await LocationsServiceAPI.GetLocationDevices(id, request);
			if (response.data) setDevices(response.data.items);
		} catch (e) {
			errorDialog(e.message);
		}
	};

	const getLocation = async () => {
		if (id) {
			const e = await fetchEntity(id);
			if (e) {
				setEntity(e);
			} else setNoData(true);
		}
	};

	useEffect(() => {
		getLocation();
		fetchDevices();

		// eslint-disable-next-line
	}, []);

	const entityTabsProps: IEntityTabsProps = {
		tabs: [
			{
				header: {
					icon: <GppGoodOutlinedIcon style={iconStyle} fillColor={svgColorFill} />,
					label: t('menu.goals'),
					visible: true,
				},
				content: <LocationGoals />,
			},
			{
				header: {
					icon: <AssignmentOutlinedIcon style={iconStyle} />,
					label: t('menu.rules'),
					visible: true,
				},
				content: <LocationRules />,
			},
			{
				header: {
					icon: <VoucherIcon style={iconStyle} fillColor={svgColorFill} />,
					label: t('location.tabs.vouchers'),
					visible: true,
				},
				content: <LocationVouchers />,
			},
			{
				header: {
					icon: <PriceTagIcon style={iconStyle} fillColor={svgColorFill} />,
					label: t('menu.reports'),
					visible: true,
				},
				content: <LocationReports />,
			},
			{
				header: {
					icon: <CropOriginalIcon style={iconStyle} />,
					label: t('menu.graphics'),
					visible: true,
				},
				content: <LocationGraphics entity={entity} onRefresh={getLocation} />,
			},
		],
	};

	const detailsProps: IEntityDetailsProps = {
		detailTitle: entity?.name || '',
		entityType: t('location.headerName'),
		entity: entity,
		api: LocationsServiceAPI,
		isEditable: false,
		isRemovable: false,
		openModal: () => {},
		entityDetailsComponent: <></>,
		entityActionIconsComponent: <></>,
		entitySubsetTableComponent: (
			<>
				{entity ? (
					<div style={{ display: 'flex', justifyContent: 'space-between' }}>
						<div className={classes.details} style={{ marginRight: '15px' }}>
							<DetailsBox
								icon={LocationDetailsIcon}
								schema={[
									{
										title: t('location.locationData'),
										dataElement: <span>{`ul. ${entity.street}, ${entity.zipCode} ${entity.city}`}</span>,
										rowWithIcon: entity.locationCode
											? [
													{ icon: <LocationChainIcon />, text: entity.corporationName },
													{ icon: <LocationCodeIcon />, text: entity.locationCode },
											  ]
											: [{ icon: <LocationChainIcon />, text: entity.corporationName }],
									},
								]}
							/>
						</div>
						{devices && devices.length > 0 ? (
							<div className={classes.details}>
								<DetailsBox
									icon={LocationDevicesIcon}
									schema={[
										{
											title: t('device.device'),
											dataElement: (
												<>
													{devices.map((device) => {
														return (
															<div style={{ marginBottom: '5px' }}>
																<span>{device.name}</span>
																<div style={{ alignItems: 'center', display: 'flex', margin: '0 0 0 2px' }}>
																	<LocationChainIcon />
																	<div style={{ marginLeft: '5px' }}>{device.identifier}</div>
																</div>
															</div>
														);
													})}
												</>
											),
										},
									]}
								/>
							</div>
						) : null}
					</div>
				) : null}
				<EntityTabs {...entityTabsProps} />
			</>
		),
		headerButtons: <></>,
	};
	return noData ? <DetailsError /> : <EntityDetails {...detailsProps} />;
};
